import { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Icon } from '../Icon';

export type LightboxMedia = {
  url: string;
};

type Props = {
  media: LightboxMedia | null;
  onClose: () => void;
};

// パン操作をタップと誤認しないための移動距離の閾値（px）
const CLOSE_TAP_MAX_DRAG_PX = 10;

// NOTE: line-ban-male/src/components/MediaLightbox/index.tsx とほぼ同一実装。
// ロジックを修正するときは両方を同期すること。
export function MediaLightbox({ media, onClose }: Props) {
  // 読み込み結果は media オブジェクト参照に紐づける。開くたびに新しいオブジェクトが
  // 渡るため、開き直しはリセット用 effect なしで初期状態（再試行）から始まり、
  // 古いエラー表示が描画されることもない
  const [errorMedia, setErrorMedia] = useState<LightboxMedia | null>(null);
  const [loadedMedia, setLoadedMedia] = useState<LightboxMedia | null>(null);
  const hasError = !!media && errorMedia === media;
  const isLoaded = !!media && loadedMedia === media;
  const pointerDownPosition = useRef<{
    x: number;
    y: number;
    imageRect: DOMRect | null;
  } | null>(null);

  return (
    <Transition appear show={!!media} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={onClose}
        aria-label="画像プレビュー"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            aria-label="閉じる"
          >
            <Icon icon="close" width={20} height={20} />
          </button>
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="flex items-center justify-center">
                {media && hasError ? (
                  <p className="px-6 text-center text-white">
                    画像を表示できませんでした
                  </p>
                ) : media ? (
                  <div
                    data-testid="lightbox-zoom-area"
                    className="relative"
                    onPointerDown={(event) => {
                      const img = event.currentTarget.querySelector('img');
                      pointerDownPosition.current = {
                        x: event.clientX,
                        y: event.clientY,
                        imageRect: img ? img.getBoundingClientRect() : null,
                      };
                    }}
                    onClick={(event) => {
                      // ダブルクリック（ズーム操作）の2回目の click では閉じない
                      if (event.detail > 1) return;
                      // 画像上のクリックは無視し、黒背景クリックのみ閉じる
                      // （ライブラリが img の pointer-events を無効化するため座標で判定）
                      // 矩形はクリック開始（pointerdown）時点のものを優先する。ダブルクリックの
                      // ズームアウトでは click 評価前に画像が縮んでおり、click 時点の矩形では
                      // 画像上のクリックを背景クリックと誤認して閉じてしまうため
                      const img = event.currentTarget.querySelector('img');
                      const rect =
                        pointerDownPosition.current?.imageRect ??
                        img?.getBoundingClientRect() ??
                        null;
                      if (rect) {
                        // レイアウト未確定（読み込み中）のクリックは誤操作とみなし閉じない
                        if (rect.width === 0 || rect.height === 0) return;
                        if (
                          event.clientX >= rect.left &&
                          event.clientX <= rect.right &&
                          event.clientY >= rect.top &&
                          event.clientY <= rect.bottom
                        ) {
                          return;
                        }
                      }
                      // パン操作後の click を閉じる操作と誤認しない
                      const down = pointerDownPosition.current;
                      if (
                        down &&
                        Math.hypot(
                          event.clientX - down.x,
                          event.clientY - down.y,
                        ) > CLOSE_TAP_MAX_DRAG_PX
                      ) {
                        return;
                      }
                      onClose();
                    }}
                  >
                    {!isLoaded && (
                      <div
                        data-testid="lightbox-loading"
                        className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
                      </div>
                    )}
                    <TransformWrapper
                      key={media.url}
                      minScale={1}
                      maxScale={4}
                      doubleClick={{ mode: 'toggle' }}
                      centerOnInit
                    >
                      <TransformComponent
                        wrapperStyle={{ width: '100vw', height: '100vh' }}
                        contentStyle={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src={media.url}
                          alt="拡大画像"
                          className="max-h-[90vh] max-w-[95vw] rounded-lg object-contain"
                          onLoad={() => setLoadedMedia(media)}
                          onError={() => setErrorMedia(media)}
                        />
                      </TransformComponent>
                    </TransformWrapper>
                  </div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
